import tinycolor from "tinycolor2";

export function detectBtnColor(): {
  backgroundColor: string;
  color: string;
  borderColor: string;
} | null {
  const colors: {
    backgroundColor: string;
    color: string;
    classList: string;
  }[] = [];

  const transparentPattern = new RegExp(
    /rgba\((\d+),\s*(\d+),\s*(\d+),\s*(0(\.0*)?)\)/,
  );

  document.querySelectorAll("button").forEach((button) => {
    const el = window.getComputedStyle(button);
    const backgroundColor = el.getPropertyValue("background-color");
    const color = el.getPropertyValue("color");
    const display = el.getPropertyValue("display");

    if (display !== "none" && !transparentPattern.test(backgroundColor)) {
      colors.push({
        backgroundColor,
        color,
        classList: button.classList.toString(),
      });
    }
  });

  if (colors.length === 0) {
    return null;
  }

  const buttonClassPattern = new RegExp(/btn.*|.*button.*/i);
  const isNotButtonClass = colors.every(
    (color) => !buttonClassPattern.test(color.classList),
  );

  const filteredColors = isNotButtonClass
    ? colors
    : colors.filter((color) => buttonClassPattern.test(color.classList));

  if (filteredColors.length === 0) {
    return null;
  }

  if (filteredColors.length === 1) {
    return getButtonStyle(
      filteredColors[0].backgroundColor,
      filteredColors[0].color,
    );
  }

  const { mostFrequentColor } = filteredColors.reduce<{
    mostFrequentColor: { backgroundColor: string; color: string };
    maxCount: number;
    colors: Record<string, number>;
  }>(
    (acc, item) => {
      const color = item.backgroundColor;

      acc.colors[color] = (acc.colors[color] || 0) + 1;

      if (acc.colors[color] > acc.maxCount) {
        acc.maxCount = acc.colors[color];
        acc.mostFrequentColor = {
          backgroundColor: item.backgroundColor,
          color: item.color,
        };
      }

      return acc;
    },
    { colors: {}, mostFrequentColor: null, maxCount: 0 },
  );

  return getButtonStyle(
    mostFrequentColor.backgroundColor,
    mostFrequentColor.color,
  );
}

export async function detectFavicon(): Promise<string | null> {
  try {
    const src =
      document.querySelector<HTMLLinkElement>('link[rel~="icon"]')?.href ||
      new URL("/favicon.ico", window.location.origin).toString();

    const { width, height } = await getImageDimension(src);

    if (width < 35 || height < 30) {
      return null;
    }

    return src;
  } catch {
    return null;
  }
}

export function detectFont(): string {
  const computedStyle = window.getComputedStyle(document.body);
  return computedStyle.getPropertyValue("font-family");
}

function getImageDimension(
  src: string,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
  });
}

export function isColorCloseToWhite(color: tinycolor.Instance): boolean {
  const rgbColor = color.toRgb();
  const average = (rgbColor.r + rgbColor.g + rgbColor.b) / 3;

  return average >= 245;
}

export function getButtonStyle(
  backgroundColor: string,
  color?: string,
): { backgroundColor: string; color: string; borderColor: string } {
  const backgroundBtnColor = tinycolor(backgroundColor);
  if (!backgroundBtnColor.isValid()) {
    return null;
  }

  const btnBackground = backgroundBtnColor.toHexString();
  const btnTextFromBackground = backgroundBtnColor.isLight()
    ? "black"
    : "white";

  let textColor = btnTextFromBackground;

  if (color && tinycolor(color).isValid()) {
    const btnColor = tinycolor(color).toHexString();

    textColor =
      tinycolor.isReadable(backgroundColor, btnColor) &&
      btnBackground !== btnColor
        ? btnColor
        : btnTextFromBackground;
  }

  const borderColor = isColorCloseToWhite(backgroundBtnColor)
    ? "black"
    : btnBackground;

  return {
    backgroundColor: btnBackground,
    borderColor: borderColor,
    color: textColor,
  };
}
